import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from "../styles/newspressdetails.module.scss";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import Col from "react-bootstrap/Col";
import CustomImage from "./CustomImage";
import format from "date-fns/format";
import fetchUtil from "../utils/fetchUtil";
import { formatOrder } from "../utils/constants";
import { useRouter } from "next/router";
import Overlay from "react-bootstrap/Overlay";
import Tooltip from "react-bootstrap/Tooltip";
import { isFrappeOrJotFormClass, parseHeroHTML, getMobileImageUrl } from "../utils/helpers";
import Head from "next/head";
import LazyReveal from "./LazyReveal";
import BlogShimmer from "./BlogShimmer";
import NewsList from "./BlogDetailPage/NewsList";
import BlogsList from "./BlogDetailPage/BlogsList";
import LeadGenForms from "./EnquiryModal/LeadGenForms";
import SocialShareButtons from "./SocialShareButtons";
import AdvertisementSection from "./AdvertisementSection";
import RelatedBlogs from "./RelatedBlogs";
import NeedGuidance from "./NeedGuidance";
import { useSmoothScroll } from "../hooks/useSmoothScroll"; // web-539 fix for scroll
import useIsMobile from "../hooks/useIsMobile";
import { getOnboardingOrFormCtaProps } from "../utils/applyCtaButtonProps";
import { PRIMARY_SOURCE_B2C_WIDGET } from "../utils/b2c/constants";
import { persistSourceContext } from "../utils/b2c/sourceTracking";
import LatestNews from "./News/LatestNews";
import useLatestNews from "../hooks/useLatestNews";
import IntroAd, { IntroAdData } from "./universityDetail/IntroAd/IntroAd";
import PrefSrcButton from "./UI/PrefSrcButton/PrefSrcButton";

interface Advertisement {
  url: string;
  link: string;
}

const settings = {
  dots: false,
  infinite: true,
  speed: 500,
  slidesToScroll: 1,
  autoplay: true,
  cssEase: "linear",
  pauseOnHover: true,
  arrows: false,
  responsive: [
    {
      breakpoint: 767,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
};

const NewsAndPressDetailPage: React.FC = (props: any) => {
  const target = useRef(null);
  const router = useRouter();
  const isMobile = useIsMobile(768);
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const detailData = props.pageData[0];
  const categoryPageDataAdvertisement = props.categoryData;
  const [categoryBlogs, setCategoryBlogs] = useState<any>([]);

  const [matchedAdvertisement, setMatchedAdvertisement] = useState<Advertisement | null>(null);
  const [introAd, setIntroAd] = useState<IntroAdData>({})

  const sanitizedContent = detailData.description.replace(/&nbsp;/g, " ");

  const pathCondition = router.pathname.includes("/news/");

  const [isLatestNewsVisible, setIsLatestNewsVisible] = useState(false);
  const onLatestNewsVisible = useCallback(() => {
    setIsLatestNewsVisible(true);
  }, []);
  const { latestNews, isLoading: isLatestNewsLoading } = useLatestNews(pathCondition && isLatestNewsVisible, detailData?.id);

  useEffect(() => {
    const blogCategory = detailData?.stu_dest_categories[0]?.category;
    const coaching = detailData?.coachingCategory;
    const successStories = detailData?.successStoriesCategory;
    const recruiters = detailData?.forRecruiters;

    const categoryCondition = blogCategory ? `&stu_dest_categories.category_contains=${blogCategory}` : "";
    const courseCondition = detailData?.course ? `&course=${detailData.course}` : "";
    const coachingCondition = coaching ? `&coachingCategory=${coaching}` : "";
    const successStoriesCondition = successStories ? `&successStoriesCategory=${successStories}` : "";
    const recruiterCondition = recruiters ? `&forRecruiters=${recruiters}` : "";
    const limitCondition = "&_limit=2";
    const currentSlug = detailData?.slug;

    if (detailData?.categories && Array.isArray(categoryPageDataAdvertisement)) {
      detailData?.categories.forEach((detailCategory: any) => {
        const matchedCategory = categoryPageDataAdvertisement.find((category) => category.id === detailCategory.id);
        if (matchedCategory && matchedCategory.advertisement) {
          const advertisementImageUrl = matchedCategory.advertisement.advertisementImage?.url;
          const advertisementLink = matchedCategory.advertisement.link;
          setIntroAd(matchedCategory.advertisement?.introAd ?? {})
          if (advertisementImageUrl) {
            setMatchedAdvertisement({
              url: advertisementImageUrl,
              link: advertisementLink,
            });
          }
        }
      });
    }

    fetchUtil(
      `${process.env.NEXT_PUBLIC_API_BASE_URL}/blogs?_sort=date:DESC&_start=0&stu_dest_categories.category_ncontains=News&slug_ncontains=${currentSlug}${limitCondition}${categoryCondition}${courseCondition}${coachingCondition}${successStoriesCondition}${recruiterCondition}`,
      "GET",
      {},
      "",
      false,
    ).then((res: any) => {
      setCategoryBlogs(res);
    });
  }, [categoryPageDataAdvertisement]);

  // web-539 fix for scroll - Use smooth scroll hook for Table of Contents
  // Re-runs when blog content changes (navigation between blogs)
  useSmoothScroll(".blogDetails", "#sanitizedHeroContent", [detailData?.slug, detailData?.description]);

  const formatDate = (date: string) => format(new Date(date), "LLL dd, yyyy");

  const currentUrl = `${process.env.NEXT_PUBLIC_BASE_URL}${router.asPath}`;
  const onClickCopy = () => {
    navigator.clipboard.writeText(currentUrl);
    setIsCopied(true);
  };

  const applyCtaButtonProps = getOnboardingOrFormCtaProps(router, {
    btnLink: detailData?.blogCtaButton?.link,
    formId: detailData?.blogCtaButton?.formId,
    formKeyFromEnv: "blogs-open-enquiry-form-detailed-blogs-pages",
    toOnboardingEnabled: detailData?.blogCtaButton?.toOnboardingEnabled,
    sourceUrl: typeof window !== "undefined" ? window.location.pathname : "",
  });

  const ctaBtnClickHandler = (applyCtaButtonProps: any) => (e: React.MouseEvent<HTMLButtonElement>) => {
    if (applyCtaButtonProps.mode === "onboarding") {
      persistSourceContext({
        sourceUrl: window.location.href,
        primarySource: PRIMARY_SOURCE_B2C_WIDGET,
        fifthStepVisible: detailData?.blogCtaButton?.nearestOffice ?? true,
        coachingVisible: detailData?.blogCtaButton?.lookingForCoaching ?? true,
      });
    }

    applyCtaButtonProps.onClick?.(e);
  };

  return (
    <>
      <Head>
        <link
          rel="preload"
          as="image"
          href={detailData?.imageUrl || "/images/girl.png"}
          imageSrcSet={`${getMobileImageUrl(detailData?.imageUrl) || "/images/girl.png"} 640w`}
          imageSizes="(max-width: 767px) 640px, 100vw"
          fetchPriority="high"
        />
        <link href="/images/newsandpress/abt-bg-star.svg" rel="preload" as="image" fetchpriority="high" />
      </Head>
      <div className={styles.newpressdetail}>
        <div className={styles.curve}>
          <div className={styles.content}>
            <Container className={styles.customContainer}>
              <div id="blogdetlsheading" className={styles.bg}>
                <Row className="justify-content-center">
                  <Col lg={8} md={8} xs={12}>
                    <div className={styles.heading}>
                      <Breadcrumb className={styles.breadcrumb}>
                        <Breadcrumb.Item href={pathCondition ? "/news" : "/blogs"} style={{ opacity: 0.5 }}>
                          {pathCondition ? "News and Press" : "Blog"}
                        </Breadcrumb.Item>
                        <Breadcrumb.Item href="">Detail {pathCondition ? "News" : "Blog"}</Breadcrumb.Item>
                      </Breadcrumb>
                      <h1>{detailData.title}</h1>
                      <p className={styles.date}>{formatDate(detailData.date)}</p>
                    </div>
                  </Col>
                </Row>
              </div>

              <div id="imgdiv" className={styles.imgdiv}>
                <Row className="justify-content-center">
                  <Col xs={12} md={12}>
                    <div className={styles.parentMain}>
                      <div className={styles.first}>
                        <CustomImage
                          src={detailData?.imageUrl}
                          alt={detailData.newimageUrl?.alt || "blog-image"}
                          width={1170}
                          height={400}
                          priority={true}
                          isLazyLoad={false}
                        />
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
              <div id="usersectiondtl" className={styles.usersection}>
                <Row className={styles.botBorder}>
                  {!isMobile && (
                    <Col>
                      <button className={`${styles["syj-btn"]} ${applyCtaButtonProps.formClassName}`} onClick={ctaBtnClickHandler(applyCtaButtonProps)}>
                        {detailData?.blogCtaButton?.title ?? "Book Free Study Abroad Counselling"}
                      </button>
                    </Col>
                  )}

                  <Col lg={5} md={5} sm={12} xs={12} className={styles.linksClass}>
                    <span>Share :</span>
                    <SocialShareButtons currentUrl={currentUrl} />
                    <div className={styles["img-container"]} onClick={() => onClickCopy()} ref={target}>
                      <CustomImage src="/link.svg" width={40} height={36} alt="link" />
                    </div>
                  </Col>
                  <Col xs={12} md={7} className="d-flex justify-content-start">
                    {detailData.profImageUrl && <CustomImage src={detailData.profImageUrl} width={75} height={75} alt="proImages" />}
                    {detailData.profName && <span className={styles.title}>{detailData.profName}</span>}
                  </Col>
                </Row>
              </div>
            </Container>
          </div>
        </div>

        <Container className={styles.customContainer}>
          <Row>
            <Col xl={8} lg={8} md={8} sm={12} xs={12} className={styles.padding}>
              {isMobile && <div className={styles.prefSourceBtnContainer}>
                  <PrefSrcButton />  
                </div>
              }
              {props?.category === 'news' && detailData?.advertisement?.advertisementImg?.url && <div className={styles.advertisementMobileImage}>
                <AdvertisementSection matchedAdvertisement={{url: detailData?.advertisement?.advertisementImg?.url, link: detailData?.advertisement?.link }} isMobile={true} />
              </div>}
              {props?.category !== 'news' && <div className={styles.advertisementMobileImage}>
                <AdvertisementSection matchedAdvertisement={matchedAdvertisement} isMobile={true} />
              </div>}
              <IntroAd introAd={introAd}/>
              <div className={styles.blogContent}>
                <div id="sanitizedHeroContent" className={styles.heroContent}>{parseHeroHTML(detailData.intro, 2)}</div>
                <LazyReveal shimmer={<BlogShimmer />} rootMargin="-100px">
                  <div className={styles.para + ` blogDetails`} dangerouslySetInnerHTML={{ __html: sanitizedContent }}></div>
                </LazyReveal>
              </div>
            </Col>
            <Col xl={4} lg={4} md={4} sm={4} xs={12} className={`${styles.padding} ${styles.positionRelative} d-block d-none d-sm-block d-sm-none d-md-block`}>
              <div className={styles.relatedBlogDiv}>
                {<div className={styles.prefSourceBtnContainer}>
                  <PrefSrcButton />  
                </div>}
                {props?.category !== 'news' && <div className={styles.jotForm}>
                  <div className={styles.jotFormheading}>
                    <h3 className={styles.headingBlogs}>Want to Study Abroad?</h3>

                    <p className={styles.subHeading}>KC can help – Fill in your details and we'll call you back</p>
                  </div>
                  <LeadGenForms
                    loader="shimmer"
                    mode="inline"
                    url={process.env.NEXT_PUBLIC_CRM_CF_URL + "/" + (pathCondition ? "main-enquiry-form" : "blogs-open-enquiry-form-detailed-blogs-pages")}
                    wrapperClass={pathCondition ? styles.inLineFormNews : styles.inLineFormBlogs}
                  />
                </div>}
                
                {props?.category === 'news' && detailData?.advertisement?.advertisementImg?.url && <div className={`${styles.advertisement} ${styles.advertisementNews}`}>
                  <AdvertisementSection matchedAdvertisement={{url: detailData?.advertisement?.advertisementImg?.url, link: detailData?.advertisement?.link }} isMobile={false} />
                </div>}

                {props?.category === 'news' ? (
                  <LazyReveal onVisible={onLatestNewsVisible} rootMargin="100px">
                    <LatestNews latestNews={latestNews.slice(0, 2)} isLoading={isLatestNewsLoading} />
                  </LazyReveal>
                ) : (
                  <RelatedBlogs categoryBlogs={categoryBlogs} pathCondition={pathCondition} detailData={detailData} styles={styles} />
                )}

                {props?.category !== 'news' && <div className={styles.advertisement}>
                  <AdvertisementSection matchedAdvertisement={matchedAdvertisement} isMobile={false} />
                </div>}
              </div>
            </Col>
          </Row>
        </Container>

        <Container className={`${styles.customContainer} ${styles.NeedGuidance}`}>
          <NeedGuidance
            button2Data={{
              title: detailData?.needGuidance?.button2?.title,
              link: detailData?.needGuidance?.button2?.link,
              formId:
                detailData?.meta?.jotforms[0]?.jotformid ||
                (detailData?.needGuidance?.button2?.toOnboardingEnabled ? undefined : 'blogs-open-enquiry-form-detailed-blogs-pages'),
              toOnboardingEnabled: detailData?.needGuidance?.button2?.toOnboardingEnabled,
              lookingForCoaching: detailData?.needGuidance?.button2?.lookingForCoaching,
              nearestOffice: detailData?.needGuidance?.button2?.nearestOffice,
            }}
          />
        </Container>
        <Container className={styles.customContainer}>
          <div className={styles.horizonatalLine}></div>
        </Container>

        <div id="blgrealated" className={styles.latestNew}>
          <Container className={styles.customContainer}>
            <Row>
              <div className={styles.heading}>
                <h2>Latest {pathCondition ? "News" : "Blogs"}</h2>
              </div>
            </Row>

            <div className={styles.news}>
              {pathCondition ? (
                <NewsList
                  sliderSettings={settings}
                  styles={styles}
                  detailData={detailData}
                  pathCondition={pathCondition}
                  latestNews={latestNews}
                  isLoading={isLatestNewsLoading}
                  onVisible={onLatestNewsVisible}
                />
              ) : (
                // Render blogs
                <BlogsList detailData={detailData} styles={styles} pathCondition={pathCondition} settings={settings} />
              )}
            </div>
          </Container>
        </div>
        <Overlay target={target.current} show={isCopied} placement="bottom" rootClose onHide={() => setIsCopied(false)}>
          {(props) => (
            <Tooltip id="overlay-example" {...props}>
              Link copied!
            </Tooltip>
          )}
        </Overlay>
      </div>
    </>
  );
};
export default NewsAndPressDetailPage;
